import { router } from '@inertiajs/react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import * as React from 'react';

const statusColors = {
  Pendente: 'warning',
  Quebrado: 'error',
  Disponível: 'info',
  Indisponível: 'error',
  'Em Rota Coletando': 'info',
  'Foto Recebida': 'info',
  'Retornando para o Hub': 'info',
  Concluída: 'success',
}

const handleEdit = (edit_url, trip_id) => {
  router.get(edit_url ?? `/trips/${trip_id}/edit`);
};

const handleShowDetails = (trip_id) => {
  router.get(`/trips/${trip_id}`);
};

const isToday = (selectedDate) => {
  const today = new Date().toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).split('/').reverse().join('-'); // Converte DD/MM/YYYY para YYYY-MM-DD
  return selectedDate === today;
};

// Função para verificar se uma trip está em atraso
const isDelayed = (row, selectedDate) => {
  // Só verifica atrasos para o dia atual
  if (!isToday(selectedDate)) return false;

  // Verifica se tem previsão de chegada
  if (!row.estimated_arrival_at) return false;

  // Verifica se a viagem já foi concluída
  if (row.status === 'Concluída' || row.finished_at) return false;

  // Compara a previsão com a hora atual
  const currentTime = new Date();
  const estimatedTime = new Date(`${selectedDate}T${row.estimated_arrival_at}`);

  return estimatedTime < currentTime;
};

export const getColumns = (selectedDate, currentUser, handleDeleteClick, refreshTrigger) => [
  {
    field: 'carrier_name',
    headerName: 'Transportadora',
    flex: 1,
    minWidth: 100
  },
  {
    field: 'user_name',
    headerName: 'Motorista',
    flex: 0.8,
    minWidth: 180,
  },
  {
    field: 'sequence_indicator',
    headerName: '#',
    flex: 0.3,
    minWidth: 20,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1.0,
    minWidth: 80,
    renderCell: (params) => {
      return <Chip label={params.value} color={statusColors[params.value]} variant="filled" />
    }
  },
  {
    field: 'started_at',
    headerName: 'Início',
    flex: 0.8,
    minWidth: 20,
  },
  {
    field: 'truck_picture',
    headerName: 'Foto',
    flex: 0.5,
    minWidth: 20,
    align: 'left',
    renderCell: (params) => {
      if (!params.value) return null;

      return (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 0.5
        }}><Button
          component="a"
          href={params.value}
          target="_blank"
          size="small"
          sx={{ textDecoration: 'none', color: 'inherit', marginLeft: '-8px', height: '24px' }}
        >
            Ver Foto
          </Button>
        </Box>
      )
    }
  },
  {
    field: 'estimated_arrival_at',
    headerName: 'Previsão',
    flex: 0.8,
    minWidth: 20,
    renderCell: (params) => {
      const delayed = isDelayed(params.row, selectedDate);

      return (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          height: '100%'
        }}>
          <span>{params.value}</span>
          {delayed && (
            <Tooltip title="Em atraso">
              <AccessTimeIcon
                sx={{
                  color: 'warning.main',
                  fontSize: '16px',
                  marginLeft: '2px'
                }}
              />
            </Tooltip>
          )}
        </Box>
      );
    }
  },
  {
    field: 'finished_at',
    headerName: 'Chegada',
    flex: 0.8,
    minWidth: 20,
  },
  {
    field: 'package_count',
    headerName: 'Pacotes',
    flex: 0.5,
    minWidth: 20,
  },
  {
    field: 'actions',
    headerName: 'Ações',
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    width: 160,
    renderCell: (params) => {
      // Verificar se é hoje e se o usuário não tem carrier_id
      const showActions = isToday(selectedDate) && !currentUser.carrier_id;
      return (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 0.5
        }}>
          <Tooltip title="Ver detalhes">
            <IconButton
              sx={{
                height: '30px',
                width: '35px',
              }}
              size="small"
              onClick={() => handleShowDetails(params.row.id)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {
            showActions && (
              <>
                <Tooltip title="Editar viagem">
                  <IconButton
                    sx={{
                      height: '30px',
                      width: '35px',
                    }}
                    size="small"
                    onClick={() => handleEdit(params.row.edit_url, params.row.id)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Excluir viagem">
                  <IconButton
                    sx={{
                      height: '30px',
                      width: '35px',
                    }}
                    size="small"
                    color="warning"
                    onClick={() => handleDeleteClick(params.row.destroy_url, params.row.carrier_name, params.row.user_name, params.row.sequence_indicator, params.row.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )
          }
        </Box>
      );
    },
  },
];

export default function TowersDataGrid({ rows, selectedDate, currentUser }) {
  const [deleteDialog, setDeleteDialog] = React.useState({
    open: false,
    tripInfo: '',
    destroyUrl: '',
    loading: false
  });

  // Estado para forçar re-render a cada 5 segundos e atualizar status de atraso
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);

  // Atualizar a cada 5 segundos para verificar novos atrasos
  React.useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
    }, 5000); // 5 segundos

    return () => clearInterval(interval);
  }, []);

  const handleDeleteClick = (destroy_url, carrier_name, user_name, sequence_indicator, trip_id) => {
    const tripInfo = `${carrier_name} - ${user_name} (#${sequence_indicator})`;
    setDeleteDialog({
      open: true,
      tripInfo,
      destroyUrl: destroy_url ?? `/trips/${trip_id}`,
      loading: false
    });
  };

  const handleDeleteConfirm = () => {
    setDeleteDialog(prev => ({ ...prev, loading: true }));

    router.delete(deleteDialog.destroyUrl, {
      preserveScroll: true,
      onSuccess: () => {
        setDeleteDialog({
          open: false,
          tripInfo: '',
          destroyUrl: '',
          loading: false
        });
      },
      onError: (errors) => {
        setDeleteDialog(prev => ({ ...prev, loading: false }));
      }
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      tripInfo: '',
      destroyUrl: '',
      loading: false
    });
  };

  const columns = getColumns(selectedDate, currentUser, handleDeleteClick, refreshTrigger);

  return (
    <>
      <DataGrid
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        rows={rows ?? []}
        columns={columns}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        initialState={{
          pagination: { paginationModel: { pageSize: 50 } },
        }}
        pageSizeOptions={[10, 20, 50, 100, 200, 500]}
        density="compact"
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: {
                variant: 'outlined',
                size: 'small',
              },
              columnInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              operatorInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                },
              },
            },
          },
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Tem certeza que deseja excluir a viagem{' '}
            <strong>{deleteDialog.tripInfo}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
            Esta ação não pode ser desfeita e todos os dados relacionados serão perdidos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteDialog.loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="warning"
            variant="contained"
            disabled={deleteDialog.loading}
          >
            {deleteDialog.loading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
